import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getLanguage } from '../../util/languages';
import { fetchDomains } from './api';
import { categoryColor } from './format';
import type { DomainsPage, Period } from './types';

const SIZE = 10;

type Props = {
  operatorId: string;
  category: { id: string; name: string };
  period: Period;
  cpeId?: number;
  devId?: number;
  onClose: () => void;
};

export const CategoryDrilldown = ({ operatorId, category, period, cpeId, devId, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const locale = getLanguage(i18n.language).locale;
  const root = useRef<HTMLElement>(null);

  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<DomainsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    root.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [category.id]);

  // Debounce the search box; a new term or scope goes back to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(input.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [input]);
  useEffect(() => {
    setInput('');
    setQ('');
    setPage(1);
  }, [category.id, period, cpeId, devId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchDomains(operatorId, category.id, { l: period, cpeId, devId, page, size: SIZE, q: q || undefined })
      .then((res) => !cancelled && setData(res))
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [operatorId, category.id, period, cpeId, devId, page, q]);

  const pages = data ? Math.max(1, Math.ceil(data.records / SIZE)) : 1;

  return (
    <section ref={root} className="bd-card dc-drill" style={{ '--cat': categoryColor(category.id) } as React.CSSProperties}>
      <div className="dc-drill-hd">
        <div>
          <h2 className="bd-h3">
            <span className="dc-drill-dot" /> {t('dnsCategories.drill.title', { name: category.name })}
          </h2>
          <span className="bd-meta">
            {t('dnsCategories.drill.subtitle')}
            {data && <> · {t('dnsCategories.drill.records', { count: data.records })}</>}
          </span>
        </div>
        <div className="dc-drill-tools">
          <label className="bd-search tm-filter">
            <input
              type="search"
              value={input}
              placeholder={t('dnsCategories.drill.search')}
              aria-label={t('dnsCategories.drill.search')}
              onChange={(e) => setInput(e.target.value)}
            />
          </label>
          <button type="button" className="bd-iconbtn" onClick={onClose} aria-label={t('dnsCategories.drill.close')} title={t('dnsCategories.drill.close')}>
            <X size={16} />
          </button>
        </div>
      </div>

      {error && <div className="dc-state bd-meta" role="alert">{t('dnsCategories.drill.error')}</div>}
      {loading && !data && !error && <div className="dc-state bd-meta">{t('dnsCategories.drill.loading')}</div>}
      {data && !error && data.data.length === 0 && <div className="dc-state bd-meta">{t('dnsCategories.drill.empty')}</div>}
      {data && !error && data.data.length > 0 && (
        <>
          <table className="bd-table dc-drill-table">
            <thead>
              <tr>
                <th>{t('dnsCategories.drill.domain')}</th>
                <th className="dc-num">{t('dnsCategories.drill.requests')}</th>
                <th className="dc-num">{t('dnsCategories.drill.blocked')}</th>
                <th>{t('dnsCategories.drill.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((row) => (
                <tr key={row.fqdn}>
                  <td className="bd-mono">{row.fqdn}</td>
                  <td className="dc-num bd-mono">{row.requests.toLocaleString(locale)}</td>
                  <td className="dc-num bd-mono">{row.blocked.toLocaleString(locale)}</td>
                  <td><span className={`bd-chip ${row.topReason === 'ALLOWED' ? 'mint' : 'warn'}`}>{row.topReason}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="dc-drill-pager">
            <button type="button" className="bd-pill" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t('dnsCategories.drill.prev')}
            </button>
            <span className="bd-meta">{t('dnsCategories.drill.page', { page, pages })}</span>
            <button type="button" className="bd-pill" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              {t('dnsCategories.drill.next')}
            </button>
          </div>
        </>
      )}
    </section>
  );
};
